import { Component, DoCheck, EventEmitter, Input, Output } from '@angular/core';
import {
  NewReleaseHeadingToAuction,
  HeadingToAuctionEntry,
  PlateCard,
} from '../../models/new-release-page.types';
import { platePath } from 'src/app/utils/canonical-url';

/**
 * "Heading to auction" — plates DVLA has reserved for future auction rather
 * than fixed-price sale. Surfaces them on their own dedicated surface so the
 * featured / marquee slot on the buy-focused sections (Hero / Perfect
 * Plates / Names) stays exclusively about available plates.
 *
 * Split into two sub-sections so the LLM-ranked editorial picks aren't
 * drowned by the wider DVLA-reserved name catalogue:
 *
 *   1. "DVLA's marquee picks" — scored entries (phrase_quality_score > 0),
 *      sorted by quality DESC. For Series 76 today: GO76 AME (9) +
 *      NO76 OOD (4). Marquee editorial framing on the first card.
 *   2. "Names heading to auction" — unscored name plates (phrase_quality_
 *      score === 0), sorted alphabetically by the readable name. Quality 0
 *      means "not scored" not "low quality" — these are real auction-
 *      reserved inventory (GAYLE / BOBBY / PENNY / BELLA / …) and they
 *      earn their place. Cards render plate + name + DVLA CTA only; no
 *      editorial copy — they're a category, not marquee picks.
 *
 * Section hides entirely when both sub-arrays are empty. Each sub-section's
 * grid renders only when its own array is non-empty.
 *
 * CTA: opens DVLA's own search-results page in a new tab — canonical
 * Register-Interest path for held-back plates. PlateX's /compare page is
 * not useful here (it aggregates vendor prices, and an auction-reserved
 * plate has none, so it stays in skeleton-loaders indefinitely; verified
 * on dev2 for GO76 AME).
 */
@Component({
  selector: 'pla-new-release-heading-to-auction',
  templateUrl: './new-release-heading-to-auction.component.html',
  styleUrls: ['./new-release-heading-to-auction.component.scss'],
})
export class NewReleaseHeadingToAuctionComponent implements DoCheck {
  @Input() data!: NewReleaseHeadingToAuction;
  @Input() year: string = '';
  /** Roman numeral for the section eyebrow (page chapter order). */
  @Input() ix: string = '';
  /** Paper / tint band, alternated by the page. */
  @Input() tint: boolean = false;
  /** Pre-release: the held-back predictions are real plates people can follow, so they get the
   *  same card (heart + Notify me) as the sections above. The page owns the lists. */
  @Input() isTracked: (c: PlateCard) => boolean = () => false;
  @Input() isFavourited: (c: PlateCard) => boolean = () => false;
  @Output() trackClicked = new EventEmitter<PlateCard>();
  @Output() favouriteClicked = new EventEmitter<PlateCard>();

  private cardsFor: HeadingToAuctionEntry[] | null = null;
  private cards: PlateCard[] = [];

  /** True when the list renders as standard plate cards: pre-release predictions (heart +
   *  Notify me). Released results use the same compact grid as 27's held-back list. */
  get asCards(): boolean { return this.predicted; }

  /** namesAuction as plate cards, rebuilt only when the data changes. No tag: the section
   *  title already says these are auction plates. */
  get predictedCards(): PlateCard[] {
    if (this.cardsFor !== this.namesAuction) {
      this.cardsFor = this.namesAuction;
      this.cards = this.namesAuction.map((e) => e.card ?? (this.results
        ? { plate: e.plate, word: e.word, state: 'sold', price: e.soldPrice ?? null, newIssue: false, qualityScore: 0,
            historyNote: e.soldDate ? `Sold at DVLA auction, ${this.monthOf(e.soldDate)}` : 'Sold at DVLA auction' }
        : { plate: e.plate, word: e.word, state: 'prerelease', price: null, newIssue: false, qualityScore: 0 }) as PlateCard);
    }
    return this.cards;
  }

  trackByPlate(_: number, c: PlateCard): string { return c.plate; }
  trackByEntry(_: number, e: HeadingToAuctionEntry): string { return e.plate; }

  /** Every plate links to its plate detail page (/plate/REG). */
  platePath(e: { plate: string }): string { return platePath(e.plate); }

  /** A few hundred plates is a long scroll on a phone: a first set (full cards are bigger, so
   *  fewer), then See more, or the A to Z jump, which shows every plate under that letter. */
  get pageSize(): number { return this.asCards ? 12 : 24; }
  private step(): number { return this.asCards ? 24 : 48; }
  shown = 12;
  letter: string | null = null;
  private sizedFor: boolean | null = null;

  ngDoCheck(): void {
    if (this.sizedFor !== this.asCards) { this.sizedFor = this.asCards; this.shown = this.pageSize; }
  }

  private initial(word: string): string { return (word || '').trim().charAt(0).toUpperCase(); }

  get azCounts(): Record<string, number> {
    const counts: Record<string, number> = {};
    this.namesAuction.forEach((e) => { const l = this.initial(e.word); if (l) counts[l] = (counts[l] || 0) + 1; });
    return counts;
  }

  setLetter(l: string | null): void { this.letter = l; this.shown = this.pageSize; }

  private filtered<T extends { word: string }>(list: T[]): T[] {
    return this.letter ? list.filter((e) => this.initial(e.word) === this.letter) : list;
  }
  get filteredCount(): number { return this.filtered(this.namesAuction).length; }
  get visibleCards(): PlateCard[] {
    const f = this.filtered(this.predictedCards);
    return this.letter ? f : f.slice(0, this.shown);
  }
  get visibleEntries(): HeadingToAuctionEntry[] {
    const f = this.filtered(this.namesAuction);
    return this.letter ? f : f.slice(0, this.shown);
  }
  get nextBatch(): number { return Math.min(this.step(), this.filteredCount - this.shown); }
  loadMore(): void { this.shown += this.step(); }

  get marqueePicks(): HeadingToAuctionEntry[] {
    return this.data?.marqueePicks || [];
  }

  get namesAuction(): HeadingToAuctionEntry[] {
    return this.data?.namesAuction || [];
  }

  /** Combined total — used in the section sub-copy and to hide the whole block when 0. */
  get totalCount(): number {
    return this.marqueePicks.length + this.namesAuction.length;
  }

  /** Pre-release series: nothing is held back yet — these are our predictions,
   *  so the copy says so and the DVLA Register-Interest CTA is hidden (DVLA
   *  has no record of the plate until release). */
  get predicted(): boolean {
    return !!this.data?.predicted;
  }

  /** Released series: plates DVLA still holds for a future auction, and has sold (whole series). */
  get heldCount(): number { return this.data?.heldCount || 0; }
  get soldCount(): number { return this.data?.soldTotal?.count || this.data?.soldCount || 0; }

  /** Released series: header total of every plate in the series sold at DVLA auction. */
  get soldTotal(): { count: number; pounds: number; since: string | null } | null {
    return this.results && this.data?.soldTotal?.count ? this.data.soldTotal : null;
  }

  /** Released series: actual DVLA auction results (hammer price), no CTA. */
  get results(): boolean {
    return !!this.data?.results;
  }

  /** '2022-09' (DVLA publishes auction month only) -> 'Sep 2022'. */
  monthOf(d: string): string {
    const m = /^(\d{4})-(\d{2})/.exec(d || '');
    if (!m) return d;
    return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][+m[2] - 1] + ' ' + m[1];
  }

  /** Released series: still held by DVLA (live state from the card). */
  isHeld(entry: HeadingToAuctionEntry): boolean { return entry.card?.state === 'auction'; }

  /** Released series: back on sale gets today's price, otherwise the hammer price. */
  resultLabel(entry: HeadingToAuctionEntry): string {
    const c = entry.card;
    if (c && (c.state === 'available' || c.state === 'resale') && c.price) return `For sale \u00A3${c.price.toLocaleString('en-GB')}`;
    return this.soldLabel(entry);
  }

  soldLabel(entry: HeadingToAuctionEntry): string {
    if (!entry.soldPrice) return 'Sold at DVLA auction';
    return `Sold \u00A3${entry.soldPrice.toLocaleString('en-GB')}`;
  }

  /**
   * DVLA's own search-results URL for the registration. Three query params
   * beyond `search` are required to land on the "REGISTER INTEREST" view
   * for an auction-reserved plate:
   *   - `action=results`  — without this DVLA renders the empty search form
   *   - `searched=true`   — flags the request as a real user search so the
   *                         results template is selected
   *   - `super={reg}`     — DVLA's "super search" handle; without it the
   *                         exact-match panel (the one carrying the
   *                         REGISTER INTEREST button) doesn't render
   *
   * Verified live on dvlaregistrations.dvla.gov.uk for GO76 AME — with
   * these three params the page lands directly on the matching panel +
   * Register Interest CTA. The remaining empty defaults DVLA's own UI
   * generates (pricefrom=0&priceto=&prefixmatches=&...) are not needed.
   */
  dvlaUrl(entry: HeadingToAuctionEntry): string {
    const reg = (entry.plate || '').replace(/\s+/g, '');
    const enc = encodeURIComponent(reg);
    return `https://dvlaregistrations.dvla.gov.uk/search/results.html?search=${enc}&action=results&searched=true&super=${enc}`;
  }

  /**
   * Editorial caption for the marquee-picks sub-section. Only the first
   * (highest-quality) entry gets the marquee framing; subsequent scored
   * entries get a plain framing because the marquee wording would
   * over-claim for lower scores. Names cards render no editorial — they're
   * a catalogue, not individual picks.
   */
  marqueeEditorialFor(_entry: HeadingToAuctionEntry, index: number): string {
    if (this.predicted) {
      return index === 0
        ? 'The strongest RT read of the series. Watch whether DVLA sells it or holds it for auction.'
        : 'Strong single-word read: an auction candidate.';
    }
    if (index === 0) {
      return 'The strongest single-word read of the series, held back by DVLA for future auction.';
    }
    return 'Also reserved for auction.';
  }
}
